import { z } from 'zod'

import { AllocationStatus, AllocationType } from './member-resource'

/**
 * Plan 5 Resource Planner shared types.
 *
 * These types define the over-the-wire shapes between the API (`/member-resources/planner/*`)
 * and the web client. No new allocation data model is introduced — rows fan out
 * from existing `member_allocation`, `member_utilization`, `staffing_decisions`,
 * and `allocation_history` tables.
 */

export enum PlannerGranularity {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

export enum PlannerGroupBy {
  DEPARTMENT = 'DEPARTMENT',
  ROLE = 'ROLE',
  WORKER_TYPE = 'WORKER_TYPE',
  PROJECT = 'PROJECT',
  TEAM = 'TEAM',
  CLIENT = 'CLIENT',
  MEMBER = 'MEMBER',
  NONE = 'NONE',
}

export enum BurnoutRiskLevel {
  NONE = 'NONE',
  WATCH = 'WATCH',
  ELEVATED = 'ELEVATED',
  HIGH = 'HIGH',
}

export const PlannerTimelineRequestSchema = z.object({
  start: z.string(), // ISO date (inclusive)
  end: z.string(), // ISO date (exclusive)
  granularity: z
    .nativeEnum(PlannerGranularity)
    .default(PlannerGranularity.WEEK),
  groupBy: z.nativeEnum(PlannerGroupBy).default(PlannerGroupBy.DEPARTMENT),
  memberIds: z.array(z.string().uuid()).optional(),
  projectIds: z.array(z.string().uuid()).optional(),
  dealIds: z.array(z.string().uuid()).optional(),
  /** @deprecated single-client scope; kept for backward compatibility. Prefer `entityIds`. */
  entityId: z.string().uuid().optional(),
  /** Plan 02 (planner rework): multi-client scope. Unioned with `entityId` server-side. */
  entityIds: z.array(z.string().uuid()).optional(),
  departmentIds: z.array(z.string().uuid()).optional(),
  roleIds: z.array(z.string().uuid()).optional(),
  allocationTypes: z.array(z.nativeEnum(AllocationType)).optional(),
  statuses: z.array(z.nativeEnum(AllocationStatus)).optional(),
  skillIds: z.array(z.string().uuid()).optional(),
  workerTypes: z.array(z.string()).optional(),
  utilizationMin: z.number().optional(),
  utilizationMax: z.number().optional(),
  availabilityOnly: z.boolean().optional(),
  demandGapsOnly: z.boolean().optional(),
})

export type PlannerTimelineRequest = z.infer<
  typeof PlannerTimelineRequestSchema
>

export interface PlannerAvailabilityPoint {
  periodStart: string
  availableHours: number
  timeOffHours: number
  holidayHours: number
}

export interface PlannerUtilizationPoint {
  periodStart: string
  billablePercent: number | null
  totalPercent: number | null
  calculatedAt: string | null
  /** Null when no utilization row has ever been written for this period. */
  calculationTrigger: string | null
}

export interface PlannerAllocation {
  id: string
  memberId: string | null
  projectId: string | null
  entityId: string | null
  dealId: string | null
  roleId: string | null
  allocationType: AllocationType
  status: AllocationStatus
  startDate: string
  endDate: string
  hoursPerWeek: number | null
  percentage: number | null
  project: {
    id: string
    name: string
    colorTag?: string | null
    entityId?: string | null
    /** The project's client entity name, so client groupings can be labeled. */
    entityName?: string | null
  } | null
  entity: { id: string; name: string } | null
  deal: {
    id: string
    name: string
    /** The deal's client entity, when set — lets deal allocations nest under their client. */
    entityId?: string | null
    entityName?: string | null
  } | null
  demandRequestId: string | null
  notes: string | null
  updatedBy: string | null
  updatedAt: string
}

/**
 * Plan 01 (planner rework) — actuals attributed to a single target within one
 * member-period bucket. Targets mirror allocation scoping: exactly one of
 * projectId/entityId/dealId is set, or `targetless` is true when the time
 * entry's module could not be mapped to a project/client/deal (e.g. time
 * logged against a ticket or with no module). Targetless hours still count
 * toward the member-period `actualHours` total so totals stay honest.
 * A target appearing here does NOT imply an allocation exists for it —
 * time logged to an unallocated project is returned under that project.
 */
export interface PlannerActualsTarget {
  projectId: string | null
  /** For project targets this is the project's client entity (when set), enabling client grouping. */
  entityId: string | null
  dealId: string | null
  targetless: boolean
  actualHours: number
  /** Resolved server-side so actuals-only targets (no allocation in window) can still be labeled. */
  projectName?: string | null
  entityName?: string | null
  dealName?: string | null
}

/**
 * Per member-period metrics at the request granularity.
 * `actualHours` is null when the period starts after today (FR-G3: future
 * periods render "—" even if stray entries exist); 0 means a past/current
 * period with no logged time.
 */
export interface PlannerPeriodMetrics {
  /** ISO date — start of the period bucket (day/week/month per request granularity). */
  periodStart: string
  /** Available hours for the period: weekly schedule minus time off/holidays minus confirmed PTO allocations. */
  capacityHours: number
  actualHours: number | null
  /** Logged but not yet submitted (DRAFT); not part of `actualHours`. Null
   *  for future periods, like `actualHours`. */
  draftHours?: number | null
  byTarget: PlannerActualsTarget[]
}

export interface PlannerTimelineMember {
  memberId: string
  name: string
  avatarUrl: string | null
  primaryRole: { id: string; name: string } | null
  department: { id: string; name: string } | null
  workerType: string | null
  maxProjectCount: number | null
  activeProjectCount: number
  /** Money field consumed by the heatmap REVENUE mode — removed in plan 06 (mode relocates to Utilization tab, plan 09). */
  revenueTarget: number | null
  burnoutRiskLevel: BurnoutRiskLevel
  burnoutStreakWeeks: number
  baseWeeklyCapacityHours: number
  availability: PlannerAvailabilityPoint[]
  utilization: PlannerUtilizationPoint[]
  allocations: PlannerAllocation[]
  /** Plan 01: per-period actuals + capacity at the request granularity, covering the request window. */
  periodMetrics: PlannerPeriodMetrics[]
}

export interface PlannerTimelineResponse {
  window: {
    start: string
    end: string
    granularity: PlannerGranularity
    weekStartsOn: number
  }
  members: PlannerTimelineMember[]
  organization: {
    conflictThresholdPercent: number
    stalenessThresholdHours: number
    staleCount?: number
    /** ISO timestamp of the oldest calculatedAt among visible members; null when no utilization rows exist. */
    oldestCalculatedAt?: string | null
  }
}

export interface HourOverallocationConflict {
  memberId: string
  periodStart: string
  availableHours: number
  allocatedHours: number
  overagePercent: number
  contributingAllocations: Array<{
    allocationId: string
    projectId: string | null
    entityId: string | null
    hoursPerWeek: number
    status: AllocationStatus
  }>
}

export interface AccountOverloadConflict {
  memberId: string
  activeProjectCount: number
  maxProjectCount: number
  projects: Array<{ id: string; name: string }>
}

export interface PlannerConflictsResponse {
  hourConflicts: HourOverallocationConflict[]
  accountOverloadConflicts: AccountOverloadConflict[]
}

export const AvailabilityFinderRequestSchema = z.object({
  roleId: z.string().uuid(),
  startDate: z.string(),
  endDate: z.string(),
  hoursPerWeek: z.number().positive(),
  skillIds: z.array(z.string().uuid()).optional(),
  workerType: z.string().optional(),
})

export type AvailabilityFinderRequest = z.infer<
  typeof AvailabilityFinderRequestSchema
>

export interface AvailabilityFinderResult {
  memberId: string
  name: string
  avatarUrl: string | null
  roleId: string | null
  roleName: string | null
  workerType: string | null
  availableHoursPerWeek: number
  utilizationTrend: number[]
  /** Null when caller lacks TEAM.READ permission. */
  costRate: number | null
  activeProjectCount: number
  maxProjectCount: number | null
  atAccountLimit: boolean
  burnoutRiskLevel: BurnoutRiskLevel
  fitScore: number
  subScores: {
    capacityScore: number
    roleMatchScore: number
    skillMatchScore: number
    utilizationProximity: number
    accountLoadScore: number
    burnoutPenalty: number
    familiarityScore: number
    /** Null when caller lacks TEAM.READ permission. */
    costRateScore: number | null
  }
}

export type StaffingDecisionChange =
  | {
      kind: 'create'
      allocation: {
        memberId?: string | null
        projectId?: string | null
        entityId?: string | null
        dealId?: string | null
        allocationType?: AllocationType
        roleId?: string | null
        startDate: string
        endDate: string
        hoursPerWeek?: number | null
        allocationPercent?: number | null
        status: AllocationStatus
        notes?: string | null
      }
    }
  | {
      kind: 'patch'
      allocationId: string
      /**
       * Plan 08 (FR-X6): the allocation's `updatedAt` captured when the planner
       * loaded, for optimistic-concurrency. When present and it no longer matches
       * the row's current `updatedAt`, the save rejects this item as a stale
       * conflict instead of clobbering the other editor's change. Omit to force
       * (the "keep yours" resolution re-sends with the refreshed baseline).
       */
      baselineUpdatedAt?: string
      patch: {
        hoursPerWeek?: number | null
        allocationPercent?: number | null
        startDate?: string
        endDate?: string
        roleId?: string | null
        projectId?: string | null
        entityId?: string | null
        dealId?: string | null
        allocationType?: AllocationType
        notes?: string | null
      }
    }
  | {
      kind: 'transition'
      allocationId: string
      toStatus: AllocationStatus
      reason?: string | null
      /** Plan 08 (FR-X6): see `baselineUpdatedAt` on the patch variant. */
      baselineUpdatedAt?: string
    }

export interface StaffingDecisionRequest {
  reason?: string | null
  changes: StaffingDecisionChange[]
}

/**
 * Plan 08 (FR-X6) — the current server-side snapshot of a conflicted allocation
 * ("theirs"), returned so the client can render a theirs-vs-yours choice.
 */
export interface StaffingDecisionConflictSnapshot {
  hoursPerWeek: number | null
  startDate: string
  endDate: string
  status: AllocationStatus
  updatedAt: string
  updatedBy: string | null
}

/**
 * Plan 08 (FR-X5/X6) — per-item outcome, aligned to the request by `index`
 * (position in `request.changes`). The save is partial-commit: non-conflicting
 * items apply while stale ones are rejected with their current snapshot.
 */
export type StaffingDecisionItemResult =
  | {
      index: number
      status: 'applied'
      allocationId: string
      historyRowId: string
    }
  | {
      index: number
      status: 'conflict'
      allocationId: string
      theirs: StaffingDecisionConflictSnapshot
    }

export interface StaffingDecisionResponse {
  /** Null when nothing committed (every item was a stale conflict). */
  decisionId: string | null
  appliedChanges: Array<{ allocationId: string; historyRowId: string }>
  /**
   * Plan 08: per-item results aligned by `index` to `request.changes`. Present
   * on every response; clients map `index` back to their pending changes to
   * clear the committed ones and surface conflicts for the rest.
   */
  results: StaffingDecisionItemResult[]
  committedCount: number
  conflictCount: number
}

export interface StaffingDecisionSummary {
  id: string
  reason: string | null
  decidedBy: { id: string; name: string | null } | null
  decidedAt: string
  changeCount: number
  allocationIds: string[]
}

export interface StaffingDecisionFeedResponse {
  items: StaffingDecisionSummary[]
  nextCursor: string | null
}

export interface StaffingDecisionAffectedAllocation {
  allocationId: string
  title: string
  description: string
}

export interface StaffingDecisionHistoryEntry {
  id: string
  allocationId: string
  action: string
  previousValues: Record<string, unknown> | null
  newValues: Record<string, unknown> | null
  reason: string | null
  changedAt: Date
  changedBy: { id: string; name: string | null } | null
  allocation: {
    id: string
    memberId: string | null
    projectId: string | null
    entityId: string | null
  } | null
}

export interface StaffingDecisionDetail extends StaffingDecisionSummary {
  affectedAllocations: StaffingDecisionAffectedAllocation[]
  history: StaffingDecisionHistoryEntry[]
}

export interface PlannerExportRequest {
  format: 'PDF' | 'CSV' | 'XLSX'
  window: { start: string; end: string; granularity: PlannerGranularity }
  filters?: Record<string, unknown>
  groupBy: PlannerGroupBy
  viewMode: 'TIMELINE' | 'GRID' | 'HEATMAP'
}
